import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { SharingModalProps } from 'src/app/pages/starterDashboardV2/shared/components';

import type { PlatformType, ReadyToSharePost } from './platformGrouping';

type BaseProps = Pick<SharingModalProps, 'petition' | 'trackingProps'>;
type SharingProps = Omit<SharingModalProps, 'isOpen'>;

type MessageBody = {
	body?: string;
};

export function extractMessage(message: string | null): string {
	if (!message) return '';

	try {
		const parsed = JSON.parse(message) as MessageBody;
		if ('body' in parsed) {
			return parsed.body || '';
		}
	} catch {
		// Not JSON, use message as-is
	}

	return message;
}

function getBaseProps(petition: StarterDashboardV2PageData['petition']): BaseProps {
	return {
		petition,
		trackingProps: {
			context: 'petition_dashboard_ready_to_share_post' as const,
			viewContext: 'share' as const,
		},
	};
}

function getImageUrl(
	post: ReadyToSharePost,
	petition: StarterDashboardV2PageData['petition'],
	preferredImageType: 'post' | 'story' = 'post',
): string {
	// First try to find image matching the preferred type and not processing
	const matchingImage = post.images.find(
		(img) => img.imageType.toLowerCase() === preferredImageType.toLowerCase() && !img.processing,
	);

	if (matchingImage) return matchingImage.url;

	// Fallback: use first non-processing image
	const anyImage = post.images.find((img) => !img.processing);
	if (anyImage) return anyImage.url;

	// Final fallback: petition share visuals or empty string
	return petition.shareVisuals[0]?.url || '';
}

function mapStoryProps(
	post: ReadyToSharePost,
	platform: PlatformType,
	petition: StarterDashboardV2PageData['petition'],
): SharingProps {
	return {
		...getBaseProps(petition),
		platform: platform === 'facebook' ? ('facebook' as const) : ('instagram' as const),
		postType: 'story' as const,
		sharingResource: {
			shareUrl: `https://change.org/p/${petition.slug}`,
			previewImageUrl: getImageUrl(post, petition, 'story'),
		},
	} as SharingProps;
}

function mapGroupPostProps(
	post: ReadyToSharePost,
	platform: PlatformType,
	petition: StarterDashboardV2PageData['petition'],
): SharingProps {
	return {
		...getBaseProps(petition),
		platform: platform === 'facebookgroups' ? ('facebook' as const) : ('reddit' as const),
		postType: 'post' as const,
		sharingResource: {
			message: extractMessage(post.message),
			previewImageUrl: getImageUrl(post, petition, 'post'),
			shareUrl: `https://change.org/p/${petition.slug}`,
		},
		recommendedGroup: {
			name: '',
			url: '',
			metadata: { memberCount: 0, imageUrl: '', source: '' },
		},
	} as SharingProps;
}

function mapRegularPostProps(
	post: ReadyToSharePost,
	platform: PlatformType,
	petition: StarterDashboardV2PageData['petition'],
): SharingProps {
	return {
		...getBaseProps(petition),
		platform: platform === 'facebook' ? ('facebook' as const) : ('instagram' as const),
		postType: 'post' as const,
		sharingResource: {
			message: extractMessage(post.message),
			previewImageUrl: getImageUrl(post, petition, 'post'),
			shareUrl: `https://change.org/p/${petition.slug}`,
		},
	} as SharingProps;
}

export function mapToSharingModalProps(
	post: ReadyToSharePost,
	platform: PlatformType,
	petition: StarterDashboardV2PageData['petition'],
): SharingProps {
	const isStory = post.postTypeName.toLowerCase() === 'story';
	const isGroupPost = platform === 'facebookgroups' || platform === 'reddit';

	if (isStory) return mapStoryProps(post, platform, petition);
	if (isGroupPost) return mapGroupPostProps(post, platform, petition);
	return mapRegularPostProps(post, platform, petition);
}
